import { createSystem, defineConfig, defaultConfig } from "@chakra-ui/react";
import { type SystemContext } from "@chakra-ui/react";

const config = defineConfig({
  cssVarsRoot: ":root",
  strictTokens: false, // Allow referencing non-existent tokens for flexibility // After change must run chakra typegen to update types
  conditions: {
    light: ".light &",
    dark: ".dark &",
  },
  theme: {
    keyframes: {
      shakeX: {
        "0%": { transform: "translateX(0)" },
        "5%": { transform: "translateX(-3px)" },
        "10%": { transform: "translateX(3px)" },
        "15%": { transform: "translateX(-2px)" },
        "20%": { transform: "translateX(0)" },
        "100%": { transform: "translateX(0)" },
      },
    },
    tokens: {
      borderWidths: {
        1: { value: "1px" },
        2: { value: "2px" },
        3: { value: "3px" },
        4: { value: "4px" },
      },
      colors: {
        background: {
          light: { value: "#f4f4f5" },
          dark: { value: "#18181b" },
        },
        publicFont: {
          light: { value: "#4b5563" },
          dark: { value: "#d1d5db" },
        },
        publicGradientFrom: {
          light: { value: "#eff6ff" },
          dark: { value: "#1e3a8a" },
        },
        publicHero: {
          blue: { value: "#bfdbfe" },
          green: { value: "#bbf7d0" },
          neutral: { value: "#e5e7eb" },
          red: { value: "#fecaca" },
        },
        publicSurface: {
          base: { value: "#ffffff" },
          subtle: { value: "#f9fafb" },
          muted: { value: "#f3f4f6" },
          border: { value: "#e5e7eb" },
          accent: { value: "#eff6ff" },
          accentStrong: { value: "#dbeafe" },
        },
        brand: {
          indigo: { value: "#4f46e5" },
          indigoLight: { value: "#6366f1" },
          purple: { value: "#7e22ce" },
          purpleLight: { value: "#a855f7" },
          blue: { value: "#2563eb" },
          blueLight: { value: "#3b82f6" },
        },
        action: {
          dismiss: { value: "#a1a1aa" },
          danger: { value: "#dc2626" },
          warning: { value: "#d97706" },
          success: { value: "#22c55e" },
        },
        state: {
          inactive: { value: "#e4e4e7" },
          inactiveDark: { value: "#3f3f46" },
        },
        status: {
          processed: { value: "#22c55e" },
          unprocessed: { value: "#facc15" },
        },
        prototype: {
          jobActivity: {
            accent: { value: "#16a34a" },
            bg: { value: "#dcfce7" },
            border: { value: "#86efac" },
          },
          learningOutcome: {
            accent: { value: "#db2777" },
            bg: { value: "#fce7f3" },
            border: { value: "#f9a8d4" },
          },
          workplaceCharacteristic: {
            accent: { value: "#d97706" },
            bg: { value: "#fef3c7" },
            border: { value: "#fbbf24" },
          },
          individualDescription: {
            accent: { value: "#7e22ce" },
            bg: { value: "#f3e8ff" },
            border: { value: "#c084fc" },
          },
        },
        surface: {
          bgLight: { value: "#FFFFFF" },
          bgDark: { value: "#2D3748" },
          fgLight: { value: "#1A202C" },
          fgDark: { value: "#FFFFFF" },
          borderLight: { value: "#E2E8F0" },
          borderDark: { value: "#4A5568" },
          bgLightElevated: { value: "#F7FAFC" },
          bgDarkElevated: { value: "#1A202C" },
        },
        shell: {
          bgLight: { value: "#ffffffe6" },
          bgDark: { value: "#1a202ce6" },
          borderLight: { value: "#dbe5f0" },
          borderDark: { value: "#4a5568" },
          hoverLight: { value: "#eff6ff" },
          hoverDark: { value: "#2d3748" },
          activeLight: { value: "#dbeafe" },
          activeDark: { value: "#2b6cb0" },
          mutedFgLight: { value: "#475569" },
          mutedFgDark: { value: "#cbd5e1" },
        },
        text: {
          mutedLight: { value: "#475569" },
          mutedDark: { value: "#cbd5e1" },
        },
      },
      animations: {
        shakeX: { value: "shakeX 3s ease-in-out infinite" },
      },
    },
    semanticTokens: {
      colors: {
        moveon: {
          bg: {
            value: {
              base: "{colors.background.light}",
              _dark: "{colors.background.dark}",
            },
          },
          publicFont: {
            value: {
              base: "{colors.publicFont.light}",
              _dark: "{colors.publicFont.dark}",
            },
          },
          publicGradientFrom: {
            value: {
              base: "{colors.publicGradientFrom.light}",
              _dark: "{colors.publicGradientFrom.dark}",
            },
          },
          public: {
            hero: {
              blue: {
                value: {
                  base: "{colors.publicHero.blue}",
                  _dark: "{colors.publicHero.blue}",
                },
              },
              green: {
                value: {
                  base: "{colors.publicHero.green}",
                  _dark: "{colors.publicHero.green}",
                },
              },
              neutral: {
                value: {
                  base: "{colors.publicHero.neutral}",
                  _dark: "{colors.publicHero.neutral}",
                },
              },
              red: {
                value: {
                  base: "{colors.publicHero.red}",
                  _dark: "{colors.publicHero.red}",
                },
              },
            },
            surface: {
              base: {
                value: {
                  base: "{colors.publicSurface.base}",
                  _dark: "{colors.surface.bgDarkElevated}",
                },
              },
              subtle: {
                value: {
                  base: "{colors.publicSurface.subtle}",
                  _dark: "{colors.publicSurface.subtle}",
                },
              },
              muted: {
                value: {
                  base: "{colors.publicSurface.muted}",
                  _dark: "{colors.publicSurface.muted}",
                },
              },
              border: {
                value: {
                  base: "{colors.publicSurface.border}",
                  _dark: "{colors.publicSurface.border}",
                },
              },
              accent: {
                value: {
                  base: "{colors.publicSurface.accent}",
                  _dark: "{colors.publicSurface.accent}",
                },
              },
              accentStrong: {
                value: {
                  base: "{colors.publicSurface.accentStrong}",
                  _dark: "{colors.publicSurface.accentStrong}",
                },
              },
              footerFrom: {
                value: {
                  base: "{colors.publicSurface.accentStrong}",
                  _dark: "{colors.publicSurface.accentStrong}",
                },
              },
            },
          },
          primary: {
            value: {
              base: "{colors.brand.blue}",
              _dark: "{colors.brand.blueLight}",
            },
          },
          primaryLight: {
            value: {
              base: "{colors.brand.blue/30}",
              _dark: "{colors.brand.blueLight/30}",
            },
          },
          secondary: {
            value: {
              base: "{colors.brand.purple}",
              _dark: "{colors.brand.purpleLight}",
            },
          },
          secondaryLight: {
            value: {
              base: "{colors.brand.purple/30}",
              _dark: "{colors.brand.purpleLight/60}",
            },
          },
          tertiary: {
            value: {
              base: "{colors.brand.indigo}",
              _dark: "{colors.brand.indigoLight}",
            },
          },
          tertiaryLight: {
            value: {
              base: "{colors.brand.indigo/30}",
              _dark: "{colors.brand.indigoLight/30}",
            },
          },
          // States
          inactive: {
            value: {
              base: "{colors.state.inactive}",
              _dark: "{colors.state.inactiveDark}",
            },
          },
          // Actions
          dismiss: {
            value: {
              base: "{colors.action.dismiss}",
              _dark: "{colors.action.dismiss}",
            },
          },
          dismissLight: {
            value: {
              base: "{colors.action.dismiss/30}",
              _dark: "{colors.action.dismiss/30}",
            },
          },
          dismissBorder: {
            value: {
              base: "{colors.action.dismiss/45}",
              _dark: "{colors.action.dismiss/45}",
            },
          },
          danger: {
            value: {
              base: "{colors.action.danger}",
              _dark: "{colors.action.danger}",
            },
          },
          dangerLight: {
            value: {
              base: "{colors.action.danger/30}",
              _dark: "{colors.action.danger/30}",
            },
          },
          dangerBorder: {
            value: {
              base: "{colors.action.danger/45}",
              _dark: "{colors.action.danger/45}",
            },
          },
          warning: {
            value: {
              base: "{colors.action.warning}",
              _dark: "{colors.action.warning}",
            },
          },
          warningLight: {
            value: {
              base: "{colors.action.warning/30}",
              _dark: "{colors.action.warning/30}",
            },
          },
          warningBorder: {
            value: {
              base: "{colors.action.warning/45}",
              _dark: "{colors.action.warning/45}",
            },
          },
          success: {
            value: {
              base: "{colors.action.success}",
              _dark: "{colors.action.success}",
            },
          },
          successLight: {
            value: {
              base: "{colors.action.success/30}",
              _dark: "{colors.action.success/30}",
            },
          },
          successBorder: {
            value: {
              base: "{colors.action.success/45}",
              _dark: "{colors.action.success/45}",
            },
          },
          // Status
          processed: {
            value: {
              base: "{colors.status.processed/30}",
              _dark: "{colors.status.processed/30}",
            },
          },
          unprocessed: {
            value: {
              base: "{colors.status.unprocessed/30}",
              _dark: "{colors.status.unprocessed/30}",
            },
          },
          prototype: {
            jobActivity: {
              accent: {
                value: {
                  base: "{colors.prototype.jobActivity.accent}",
                  _dark: "{colors.prototype.jobActivity.accent}",
                },
              },
              bg: {
                value: {
                  base: "{colors.prototype.jobActivity.bg}",
                  _dark: "{colors.prototype.jobActivity.accent/30}",
                },
              },
              border: {
                value: {
                  base: "{colors.prototype.jobActivity.border}",
                  _dark: "{colors.prototype.jobActivity.accent/45}",
                },
              },
            },
            learningOutcome: {
              accent: {
                value: {
                  base: "{colors.prototype.learningOutcome.accent}",
                  _dark: "{colors.prototype.learningOutcome.accent}",
                },
              },
              bg: {
                value: {
                  base: "{colors.prototype.learningOutcome.bg}",
                  _dark: "{colors.prototype.learningOutcome.accent/30}",
                },
              },
              border: {
                value: {
                  base: "{colors.prototype.learningOutcome.border}",
                  _dark: "{colors.prototype.learningOutcome.accent/45}",
                },
              },
            },
            workplaceCharacteristic: {
              accent: {
                value: {
                  base: "{colors.prototype.workplaceCharacteristic.accent}",
                  _dark: "{colors.prototype.workplaceCharacteristic.accent}",
                },
              },
              bg: {
                value: {
                  base: "{colors.prototype.workplaceCharacteristic.bg}",
                  _dark: "{colors.prototype.workplaceCharacteristic.accent/30}",
                },
              },
              border: {
                value: {
                  base: "{colors.prototype.workplaceCharacteristic.border}",
                  _dark: "{colors.prototype.workplaceCharacteristic.accent/45}",
                },
              },
            },
            individualDescription: {
              accent: {
                value: {
                  base: "{colors.prototype.individualDescription.accent}",
                  _dark: "{colors.prototype.individualDescription.accent}",
                },
              },
              bg: {
                value: {
                  base: "{colors.prototype.individualDescription.bg}",
                  _dark: "{colors.prototype.individualDescription.accent/30}",
                },
              },
              border: {
                value: {
                  base: "{colors.prototype.individualDescription.border}",
                  _dark: "{colors.prototype.individualDescription.accent/45}",
                },
              },
            },
          },
          //
          surface: {
            bg: {
              value: {
                base: "{colors.surface.bgLight}",
                _dark: "{colors.surface.bgDark}",
              },
            },
            fg: {
              value: {
                base: "{colors.surface.fgLight}",
                _dark: "{colors.surface.fgDark}",
              },
            },
            border: {
              value: {
                base: "{colors.surface.borderLight}",
                _dark: "{colors.surface.borderDark}",
              },
            },
            // Optional elevated surface
            elevatedBg: {
              value: {
                base: "{colors.surface.bgLightElevated}",
                _dark: "{colors.surface.bgDarkElevated}",
              },
            },
            toolbarBg: {
              value: {
                base: "{colors.surface.bgLightElevated}",
                _dark: "{colors.surface.bgDarkElevated}",
              },
            },
            interactiveBorder: {
              value: {
                base: "{colors.surface.borderLight}",
                _dark: "{colors.surface.borderDark}",
              },
            },
          },
          selection: {
            bg: {
              value: {
                base: "{colors.brand.purple/12}",
                _dark: "{colors.brand.purpleLight/22}",
              },
            },
            border: {
              value: {
                base: "{colors.brand.purple}",
                _dark: "{colors.brand.purpleLight}",
              },
            },
            hoverBorder: {
              value: {
                base: "{colors.brand.purple/55}",
                _dark: "{colors.brand.purpleLight/70}",
              },
            },
          },
          shell: {
            bg: {
              value: {
                base: "{colors.shell.bgLight}",
                _dark: "{colors.shell.bgDark}",
              },
            },
            border: {
              value: {
                base: "{colors.shell.borderLight}",
                _dark: "{colors.shell.borderDark}",
              },
            },
            hoverBg: {
              value: {
                base: "{colors.shell.hoverLight}",
                _dark: "{colors.shell.hoverDark}",
              },
            },
            activeBg: {
              value: {
                base: "{colors.shell.activeLight}",
                _dark: "{colors.shell.activeDark}",
              },
            },
            mutedFg: {
              value: {
                base: "{colors.shell.mutedFgLight}",
                _dark: "{colors.shell.mutedFgDark}",
              },
            },
          },
          text: {
            muted: {
              value: {
                base: "{colors.text.mutedLight}",
                _dark: "{colors.text.mutedDark}",
              },
            },
          },
        },
      },
    },
  },
});

const moveonSystem: SystemContext = createSystem(defaultConfig, config);

export default moveonSystem;
